import FieldRenderer from './FieldRenderer';
import StudentEnrollmentsSections from './StudentEnrollmentsSections';
import StudentChangeHistory from './StudentChangeHistory';
import { parsePersonName } from '../../domain/identity/parsePersonName';

/**
 * Full student (or non-student user) detail panel — same content as expanding a row on the filter users page.
 */
const StudentDetailExpandedView = ({ student: s, className = '' }) => {
  const renderField = (label, value, type = 'text') => (
    <FieldRenderer label={label} value={value} type={type} />
  );

  if (!s) return null;

  const isNonStudent = s.userType && s.userType !== 'student';

  if (isNonStudent) {
    return (
      <div className={className}>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <div className="space-y-3">
            <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide border-b pb-2">
              👤 User Information
            </h4>
            {renderField('Full Name', s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'N/A')}
            {renderField('First Name', s.firstName || 'N/A')}
            {renderField('Last Name', s.lastName || 'N/A')}
            {renderField('Email', s.emailAddress || s.email || 'N/A')}
            {renderField('User Type', s.userType || s.role || 'N/A')}
            {renderField('Is Active', s.isActive, 'boolean')}
            {renderField('User ID', s.id || s._id || null)}
            {renderField('Created At', s.createdAt || s.created_at, 'date')}
            {renderField('Updated At', s.updatedAt || s.updated_at, 'date')}
          </div>
        </div>
      </div>
    );
  }

  const fullName = s.fullName || s.full_name || '';
  const { firstName, middleName, lastName } = parsePersonName({
    firstName: s.firstName,
    lastName: s.lastName,
    fullName,
  });

  const domain = s.domain || null;

  const highestQual =
    s.academicQualifications && s.academicQualifications.length > 0
      ? s.academicQualifications.find((q) => q.isHighestDegree || q.is_highest_degree) ||
        s.academicQualifications[0]
      : null;

  const tenthQual = s.academicQualifications?.find(
    (q) => (q.qualificationType || q.qualification_type) === '10th'
  );
  const twelfthQual = s.academicQualifications?.find(
    (q) => (q.qualificationType || q.qualification_type) === '12th'
  );

  return (
    <div className={className}>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <div className="space-y-3">
          <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide border-b pb-2">
            👤 Personal Information
          </h4>
          {renderField('Full Name', fullName || `${firstName} ${middleName} ${lastName}`.trim() || 'N/A')}
          {renderField('First Name', firstName || 'N/A')}
          {renderField('Middle Name', middleName || 'N/A')}
          {renderField('Last Name', lastName || 'N/A')}
          {renderField('Email', s.emailAddress || s.email_address || s.email || 'N/A')}
          {renderField('Phone Number', s.phoneNumber || s.phone_number || s.mobile || 'N/A')}
          {renderField('Parents Name', s.parentsName || s.parents_name || s.guardianName || s.guardian_name || null)}
          {renderField('Parents Phone', s.parentsPhone || s.parents_phone || s.guardianContact || s.guardian_contact || null)}
          {renderField('Parents Email', s.parentsEmail || s.parents_email || s.guardianEmail || s.guardian_email || null)}
          {renderField('Current Address', s.currentAddress || s.current_address || s.address || 'N/A')}
          {renderField('Permanent Address', s.permanentAddress || s.permanent_address || null)}
          {renderField('Domain', domain?.domainName || null)}
        </div>

        <div className="space-y-3">
          <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide border-b pb-2">
            🎓 Academic Information
          </h4>
          {renderField('Highest Degree', highestQual?.qualificationType || highestQual?.qualification_type || null)}
          {renderField(
            'University/College',
            highestQual?.universityCollegeName || highestQual?.university_college_name || null
          )}
          {renderField('Specialization', (() => {
            const btechQual = s.academicQualifications?.find(
              (q) => (q.qualificationType || q.qualification_type) === 'B.Tech'
            );
            const spec =
              btechQual?.specializationBranch ||
              btechQual?.specialization_branch ||
              highestQual?.specializationBranch ||
              highestQual?.specialization_branch;
            return spec || null;
          })())}
          {renderField('Year of Passing', highestQual?.yearOfPassout || highestQual?.year_of_passout || null)}
          {renderField('Percentage/CGPA', (() => {
            const pct = highestQual?.percentageCgpa || highestQual?.percentage_cgpa;
            if (pct && typeof pct === 'object' && pct.d) {
              return `${pct.d.join('')}%`;
            }
            return pct ? `${pct}%` : null;
          })())}
          {renderField('Education Gap', (() => {
            const gap = highestQual?.gapJustification || highestQual?.gap_justification;
            return gap === 'Yes' ? 'Yes' : gap || null;
          })())}
        </div>

        <div className="space-y-3">
          <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide border-b pb-2">
            📊 Academic Marks
          </h4>
          {renderField('10th Marks', (() => {
            const pct = tenthQual?.percentageCgpa || tenthQual?.percentage_cgpa;
            if (pct && typeof pct === 'object' && pct.d) {
              return `${pct.d.join('')}%`;
            }
            return pct ? `${pct}%` : null;
          })())}
          {renderField('10th Year', tenthQual?.yearOfPassout || tenthQual?.year_of_passout || null)}
          {renderField('12th Marks', (() => {
            const pct = twelfthQual?.percentageCgpa || twelfthQual?.percentage_cgpa;
            if (pct && typeof pct === 'object' && pct.d) {
              return `${pct.d.join('')}%`;
            }
            return pct ? `${pct}%` : null;
          })())}
          {renderField('12th Year', twelfthQual?.yearOfPassout || twelfthQual?.year_of_passout || null)}
        </div>

        <div className="space-y-3">
          <h4 className="font-bold text-gray-800 text-sm uppercase tracking-wide border-b pb-2">
            ⚙️ System Information
          </h4>
          {renderField('VE Student ID', s.id || s._id || null)}
          {renderField('Unix username', s.unixUsername || s.unix_username || null)}
          {renderField(
            'Enrollment IDs',
            Array.isArray(s.enrollments) && s.enrollments.length > 0
              ? s.enrollments.map((e) => (e.id != null ? String(e.id) : '')).filter(Boolean).join(', ')
              : null
          )}
          {renderField('Is Active', s.isActive || s.is_active, 'boolean')}
          {renderField('Created At', s.createdAt || s.created_at, 'date')}
          {renderField('Updated At', s.updatedAt || s.updated_at, 'date')}
        </div>
      </div>

      <div className="mt-6">
        <StudentEnrollmentsSections enrollments={s.enrollments} domain={domain} repeatDomain={false} />
      </div>

      <div className="mt-6">
        <StudentChangeHistory studentId={s.id || s._id} />
      </div>
    </div>
  );
};

export default StudentDetailExpandedView;
